import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator as CalcIcon,
  X,
  Minus,
  Maximize2,
  Copy,
  Check,
  Delete,
  GripHorizontal,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { evaluateExpression, formatCalculatorResult } from '../utils/calculatorEvaluator';

export default function QuizCalculator({ isOpen, onClose }) {
  const [expression, setExpression] = useState('');
  const [currentVal, setCurrentVal] = useState('0');
  const [isCalculated, setIsCalculated] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  const containerRef = useRef(null);

  // Copy result to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentVal);
      setHasCopied(true);
      toast.success('Copied result to clipboard');
      setTimeout(() => setHasCopied(false), 1800);
    } catch {
      toast.error('Failed to copy');
    }
  }, [currentVal]);

  // Handle digit / decimal entry
  const handleNumber = useCallback((numStr) => {
    setCurrentVal((prev) => {
      if (isCalculated) {
        setIsCalculated(false);
        setExpression('');
        return numStr === '.' ? '0.' : numStr;
      }

      if (numStr === '.') {
        if (prev.includes('.')) return prev;
        return prev + '.';
      }

      if (prev === '0' || prev === 'Error' || prev === 'Cannot divide by zero') {
        return numStr;
      }
      return prev + numStr;
    });
  }, [isCalculated]);

  // Handle standard operators (+, -, ×, ÷, ^)
  const handleOperator = useCallback((op) => {
    setIsCalculated(false);
    setCurrentVal((prevVal) => {
      // If error on screen, reset first
      const val = prevVal === 'Error' || prevVal === 'Cannot divide by zero' ? '0' : prevVal;
      setExpression((prevExpr) => {
        // If expression ends with an operator, replace it
        if (/[+\-×÷^]\s*$/.test(prevExpr) && val === '0') {
          return prevExpr.replace(/[+\-×÷^]\s*$/, `${op} `);
        }
        return `${prevExpr ? prevExpr + ' ' : ''}${val} ${op} `;
      });
      return '0';
    });
  }, []);

  // Compute final equals
  const handleEquals = useCallback(() => {
    if (isCalculated) return;

    const fullExpr = `${expression} ${currentVal}`.trim();
    if (!fullExpr) return;

    const result = evaluateExpression(fullExpr);
    setExpression(`${fullExpr} =`);
    setCurrentVal(result);
    setIsCalculated(true);
  }, [expression, currentVal, isCalculated]);

  // Clear everything (All Clear)
  const handleClearAll = useCallback(() => {
    setExpression('');
    setCurrentVal('0');
    setIsCalculated(false);
  }, []);

  // Backspace last digit
  const handleBackspace = useCallback(() => {
    if (isCalculated) {
      handleClearAll();
      return;
    }
    setCurrentVal((prev) => {
      if (prev.length <= 1 || prev === 'Error' || prev === 'Cannot divide by zero') {
        return '0';
      }
      return prev.slice(0, -1);
    });
  }, [isCalculated, handleClearAll]);

  // Sign toggle (±)
  const handleToggleSign = useCallback(() => {
    setCurrentVal((prev) => {
      if (prev === '0' || prev === 'Error' || prev === 'Cannot divide by zero') return prev;
      return prev.startsWith('-') ? prev.slice(1) : '-' + prev;
    });
  }, []);

  // Percentage (%)
  const handlePercentage = useCallback(() => {
    setCurrentVal((prev) => {
      const num = parseFloat(prev);
      if (isNaN(num)) return '0';
      return formatCalculatorResult(num / 100);
    });
  }, []);

  // Square Root (√x)
  const handleSquareRoot = useCallback(() => {
    setCurrentVal((prev) => {
      const num = parseFloat(prev);
      if (isNaN(num) || num < 0) return 'Error';
      const res = Math.sqrt(num);
      return formatCalculatorResult(res);
    });
    setIsCalculated(true);
  }, []);

  // Square (x²)
  const handleSquare = useCallback(() => {
    setCurrentVal((prev) => {
      const num = parseFloat(prev);
      if (isNaN(num)) return 'Error';
      return formatCalculatorResult(num * num);
    });
    setIsCalculated(true);
  }, []);

  // Reciprocal (1/x)
  const handleReciprocal = useCallback(() => {
    setCurrentVal((prev) => {
      const num = parseFloat(prev);
      if (isNaN(num)) return 'Error';
      if (num === 0) return 'Cannot divide by zero';
      return formatCalculatorResult(1 / num);
    });
    setIsCalculated(true);
  }, []);

  // Parentheses: ( or )
  const handleParenthesis = useCallback((paren) => {
    if (paren === '(') {
      setExpression((prev) => `${prev} (`.trim());
      setCurrentVal('0');
    } else {
      setExpression((prev) => `${prev} ${currentVal} )`.trim());
      setCurrentVal('0');
    }
  }, [currentVal]);

  // Physical keyboard support
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Don't capture when typing in an active text input or textarea
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea' || document.activeElement?.isContentEditable) {
        return;
      }

      const key = e.key;

      if (key >= '0' && key <= '9') {
        e.preventDefault();
        handleNumber(key);
      } else if (key === '.') {
        e.preventDefault();
        handleNumber('.');
      } else if (key === '+') {
        e.preventDefault();
        handleOperator('+');
      } else if (key === '-') {
        e.preventDefault();
        handleOperator('-');
      } else if (key === '*' || key === 'x' || key === 'X') {
        e.preventDefault();
        handleOperator('×');
      } else if (key === '/') {
        e.preventDefault();
        handleOperator('÷');
      } else if (key === '^') {
        e.preventDefault();
        handleOperator('^');
      } else if (key === '(' || key === ')') {
        e.preventDefault();
        handleParenthesis(key);
      } else if (key === 'Enter' || key === '=') {
        e.preventDefault();
        handleEquals();
      } else if (key === 'Backspace') {
        e.preventDefault();
        handleBackspace();
      } else if (key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    handleNumber,
    handleOperator,
    handleParenthesis,
    handleEquals,
    handleBackspace,
    onClose
  ]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={containerRef}
          drag
          dragMomentum={false}
          dragElastic={0.05}
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="fixed z-50 bottom-4 right-4 sm:bottom-8 sm:right-8 w-80 sm:w-88 select-none shadow-2xl rounded-2xl bg-base-100 border border-base-300 overflow-hidden font-sans"
          style={{ touchAction: 'none' }}
        >
          {/* Header & Drag Handle */}
          <div className="flex items-center justify-between px-3.5 py-2.5 bg-base-200/80 backdrop-blur-md border-b border-base-300 cursor-grab active:cursor-grabbing">
            <div className="flex items-center gap-2 text-base-content font-medium text-sm">
              <GripHorizontal className="w-4 h-4 text-base-content/40" />
              <CalcIcon className="w-4 h-4 text-primary" />
              <span>Exam Calculator</span>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setIsMinimized((prev) => !prev)}
                className="btn btn-ghost btn-xs btn-circle text-base-content/70 hover:text-base-content"
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                {isMinimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-ghost btn-xs btn-circle text-error hover:bg-error/10"
                title="Close Calculator (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Calculator Body (Hidden when minimized) */}
          {!isMinimized && (
            <div className="p-3 bg-base-100 flex flex-col gap-2.5">
              {/* Display Box */}
              <div className="bg-base-200/60 rounded-xl p-3 border border-base-300/80 flex flex-col justify-between min-h-[72px]">
                <div className="text-xs text-base-content/60 font-mono truncate text-right h-4">
                  {expression || ' '}
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="btn btn-ghost btn-xs p-1 text-base-content/50 hover:text-primary transition-colors"
                    title="Copy result"
                  >
                    {hasCopied ? (
                      <Check className="w-3.5 h-3.5 text-success" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <div
                    className={`font-mono font-bold tracking-tight text-right truncate text-base-content ${
                      currentVal.length > 10 ? 'text-lg' : 'text-2xl'
                    }`}
                  >
                    {currentVal}
                  </div>
                </div>
              </div>

              {/* Extended Functions Toolbar (Row 1 & 2) */}
              <div className="grid grid-cols-4 gap-1.5 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => handleParenthesis('(')}
                  className="btn btn-sm btn-ghost bg-base-200/80 hover:bg-base-300 font-mono"
                  title="Open Parenthesis"
                >
                  (
                </button>
                <button
                  type="button"
                  onClick={() => handleParenthesis(')')}
                  className="btn btn-sm btn-ghost bg-base-200/80 hover:bg-base-300 font-mono"
                  title="Close Parenthesis"
                >
                  )
                </button>
                <button
                  type="button"
                  onClick={handleSquareRoot}
                  className="btn btn-sm btn-ghost bg-base-200/80 hover:bg-base-300 font-mono"
                  title="Square Root (√x)"
                >
                  √x
                </button>
                <button
                  type="button"
                  onClick={handleSquare}
                  className="btn btn-sm btn-ghost bg-base-200/80 hover:bg-base-300 font-mono"
                  title="Square (x²)"
                >
                  x²
                </button>

                <button
                  type="button"
                  onClick={() => handleOperator('^')}
                  className="btn btn-sm btn-ghost bg-base-200/80 hover:bg-base-300 font-mono"
                  title="Power (xʸ)"
                >
                  xʸ
                </button>
                <button
                  type="button"
                  onClick={handleReciprocal}
                  className="btn btn-sm btn-ghost bg-base-200/80 hover:bg-base-300 font-mono"
                  title="Reciprocal (1/x)"
                >
                  1/x
                </button>
                <button
                  type="button"
                  onClick={handlePercentage}
                  className="btn btn-sm btn-ghost bg-base-200/80 hover:bg-base-300 font-mono"
                  title="Percentage (%)"
                >
                  %
                </button>
                <button
                  type="button"
                  onClick={handleToggleSign}
                  className="btn btn-sm btn-ghost bg-base-200/80 hover:bg-base-300 font-mono"
                  title="Negate (±)"
                >
                  ±
                </button>
              </div>

              {/* Main Keypad */}
              <div className="grid grid-cols-4 gap-1.5 font-medium text-sm">
                {/* Row 1 */}
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="btn btn-sm bg-error/15 text-error hover:bg-error/25 border-none"
                  title="Clear All"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-0.5 inline" /> C
                </button>
                <button
                  type="button"
                  onClick={handleBackspace}
                  className="btn btn-sm bg-base-200/80 hover:bg-base-300 border-none"
                  title="Backspace"
                >
                  <Delete className="w-4 h-4 text-base-content/70" />
                </button>
                <button
                  type="button"
                  onClick={() => handleOperator('÷')}
                  className="btn btn-sm bg-primary/10 text-primary hover:bg-primary/20 border-none font-bold text-base"
                >
                  ÷
                </button>
                <button
                  type="button"
                  onClick={() => handleOperator('×')}
                  className="btn btn-sm bg-primary/10 text-primary hover:bg-primary/20 border-none font-bold text-base"
                >
                  ×
                </button>

                {/* Row 2 */}
                <button
                  type="button"
                  onClick={() => handleNumber('7')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  7
                </button>
                <button
                  type="button"
                  onClick={() => handleNumber('8')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  8
                </button>
                <button
                  type="button"
                  onClick={() => handleNumber('9')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  9
                </button>
                <button
                  type="button"
                  onClick={() => handleOperator('-')}
                  className="btn btn-sm bg-primary/10 text-primary hover:bg-primary/20 border-none font-bold text-base"
                >
                  −
                </button>

                {/* Row 3 */}
                <button
                  type="button"
                  onClick={() => handleNumber('4')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  4
                </button>
                <button
                  type="button"
                  onClick={() => handleNumber('5')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  5
                </button>
                <button
                  type="button"
                  onClick={() => handleNumber('6')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  6
                </button>
                <button
                  type="button"
                  onClick={() => handleOperator('+')}
                  className="btn btn-sm bg-primary/10 text-primary hover:bg-primary/20 border-none font-bold text-base"
                >
                  +
                </button>

                {/* Row 4 & 5 */}
                <button
                  type="button"
                  onClick={() => handleNumber('1')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  1
                </button>
                <button
                  type="button"
                  onClick={() => handleNumber('2')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  2
                </button>
                <button
                  type="button"
                  onClick={() => handleNumber('3')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200"
                >
                  3
                </button>
                <button
                  type="button"
                  onClick={handleEquals}
                  className="btn btn-sm btn-primary row-span-2 h-auto text-base font-bold shadow-md shadow-primary/20"
                >
                  =
                </button>

                {/* Row 5 */}
                <button
                  type="button"
                  onClick={() => handleNumber('0')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200 col-span-2"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={() => handleNumber('.')}
                  className="btn btn-sm btn-outline border-base-300 hover:bg-base-200 font-bold"
                >
                  .
                </button>
              </div>

              {/* Helpful Hint */}
              <div className="text-[10px] text-center text-base-content/40 pt-1">
                Drag anywhere to move • Keyboard enabled (Esc to close)
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
